// ── Hero image lookup ────────────────────────────────────────
import { provinceAndStateSlugMappings } from 'utils/province_or_state';
import type { ProvinceOrStateCode } from 'utils/province_or_state';
import { LocalFocusGenerationObservabilityService } from 'observability/services/local-focus-generation-observability';

const BASE = '/next/assets/images/local-focus';
/** Static bundled image used as the final fallback when no other image resolves. */
export const HERO_IMAGE_DEFAULT = `${BASE}/countries/Canada.jpg`;

const S3_BASE = 'https://zoocasa.s3.us-east-1.amazonaws.com';
const S3_CITY_BASE = `${S3_BASE}/city-images`;
const S3_STATE_PROVINCE_BASE = `${S3_BASE}/stateorprovince-images`;

export interface HeroImageUrls {
  /** Mobile (< 768px): the --sm variant, used as the default <img> src. */
  sm: string;
  /** Tablet (768px–1023px): the --md variant. */
  md: string;
  /** Desktop (>= 1024px): the full-size image with no suffix. */
  lg: string;
}

/** Country the hero location is in — the only thing that differs between the CA and US flows. */
export type HeroImageCountry = 'CA' | 'US';

export interface HeroImageGenerationInfo {
  citySlug: string;
  cityName: string;
  country: HeroImageCountry;
}

export interface HeroImageCandidate {
  sm: string;
  md?: string;
  lg?: string;
  generate?: HeroImageGenerationInfo;
}

function normalizeSlugForS3(slug: string): string {
  return slug.trim().toLowerCase().replace(/\s+/g, '-');
}

/** City hero: {S3}/city-images/{slug}--sm.jpg (mobile), --md.jpg (tablet), .jpg (desktop, no suffix). */
export function getCityHeroImageUrls(slug: string): HeroImageUrls {
  const base = `${S3_CITY_BASE}/${normalizeSlugForS3(slug)}`;
  return { sm: `${base}--sm.jpg`, md: `${base}--md.jpg`, lg: `${base}.jpg` };
}

/** Province/state hero: {S3}/stateorprovince-images/{code}.jpg (e.g. az.jpg). */
export function getProvinceOrStateHeroImageUrl(code: string): string {
  return `${S3_STATE_PROVINCE_BASE}/${code.trim().toLowerCase()}.jpg`;
}

/**
 * Ordered hero image candidates for the resolved location. HeroSearch renders
 * the first candidate and cascades to the next when one fails to load:
 *   1. City image (S3)           — when a city slug is available (generatable)
 *   2. Province/state image (S3) — when a 2-letter code is available
 *   3. Static fallback image     — always last (see `staticFallback`)
 */
export function getHeroImageCandidates(opts: {
  slug?: string;
  provinceOrStateCode?: string;
  cityName?: string;
  country: HeroImageCountry;
  /** Final static fallback image. Defaults to the bundled country default. */
  staticFallback?: string;
}): HeroImageCandidate[] {
  const candidates: HeroImageCandidate[] = [];
  if (opts.slug) {
    const city = getCityHeroImageUrls(opts.slug);
    const cityCandidate: HeroImageCandidate = { sm: city.sm, md: city.md, lg: city.lg };
    if (opts.cityName) {
      cityCandidate.generate = { citySlug: normalizeSlugForS3(opts.slug), cityName: opts.cityName, country: opts.country };
    }
    candidates.push(cityCandidate);
  }
  if (opts.provinceOrStateCode) {
    candidates.push({ sm: getProvinceOrStateHeroImageUrl(opts.provinceOrStateCode) });
  }
  candidates.push({ sm: opts.staticFallback ?? HERO_IMAGE_DEFAULT });
  return candidates;
}

/** Map of lowercase city / province / country names → static bundled hero image paths. */
export const HERO_IMAGES: Record<string, string> = {
  // Cities
  brampton: `${BASE}/popular-cities/Brampton.jpg`,
  calgary: `${BASE}/popular-cities/Calgary.jpg`,
  edmonton: `${BASE}/popular-cities/Edmonton.jpg`,
  gatineau: `${BASE}/popular-cities/Gatineau.jpg`,
  halifax: `${BASE}/popular-cities/Halifax.jpg`,
  hamilton: `${BASE}/popular-cities/Hamilton.jpg`,
  kitchener: `${BASE}/popular-cities/Kitchener.jpg`,
  laval: `${BASE}/popular-cities/Laval.jpg`,
  london: `${BASE}/popular-cities/London.jpg`,
  markham: `${BASE}/popular-cities/Markham.jpg`,
  mississauga: `${BASE}/popular-cities/Mississauga.jpg`,
  montreal: `${BASE}/popular-cities/Montreal.jpg`,
  ottawa: `${BASE}/popular-cities/Ottawa.jpg`,
  'quebec city': `${BASE}/popular-cities/Quebec City.jpg`,
  saskatoon: `${BASE}/popular-cities/Sakastoon.jpg`,
  surrey: `${BASE}/popular-cities/Surrey.jpg`,
  toronto: `${BASE}/toronto-skyline.jpg`,
  vancouver: `${BASE}/popular-cities/Vancouver.jpg`,
  vaughan: `${BASE}/popular-cities/Vaughan.jpg`,
  winnipeg: `${BASE}/popular-cities/Winnipeg.jpg`,
  // Provinces / States
  ontario: `${BASE}/province-state/Ontario.jpg`,
  alberta: `${BASE}/province-state/Alberta.jpg`,
  'british-columbia': `${BASE}/province-state/British Columbia.jpg`,
  manitoba: `${BASE}/province-state/Manitoba.jpg`,
  'new-brunswick': `${BASE}/province-state/New Brunswick.jpg`,
  'newfoundland-and-labrador': `${BASE}/province-state/New Foundland.jpg`,
  'nova-scotia': `${BASE}/province-state/Nova Scotia.jpg`,

  // Countries
  canada: `${BASE}/countries/Canada.jpg`,
};

/**
 * Resolve the best static bundled hero image for the current location.
 * Tries the city name first (e.g. "Toronto" from "Toronto, ON"),
 * then the full province name, then the city's province (from the
 * province code in the name, e.g. "ON" → "Ontario"), then the country name.
 *
 * Used by the CA local focus homepage, which serves curated static images.
 */
export function resolveHeroImage(locationName?: string, countryName?: string): string {
  if (locationName) {
    const cityPart = locationName.split(',')[0].trim().toLowerCase();
    if (HERO_IMAGES[cityPart]) {
      return HERO_IMAGES[cityPart];
    }
    const fullLower = locationName.toLowerCase().trim();
    if (HERO_IMAGES[fullLower]) {
      return HERO_IMAGES[fullLower];
    }

    // Fall back to the city's province image using the province code
    // in the name (e.g. "Toronto, ON" → "on" → "ontario" key).
    const parts = locationName.split(',');
    if (parts.length > 1) {
      const provinceCode = parts[parts.length - 1].trim().toLowerCase();
      const provinceKey = provinceAndStateSlugMappings[provinceCode as ProvinceOrStateCode];
      if (provinceKey && HERO_IMAGES[provinceKey]) {
        return HERO_IMAGES[provinceKey];
      }
    }
  }

  if (countryName) {
    const countryLower = countryName.toLowerCase().trim();
    if (HERO_IMAGES[countryLower]) {
      return HERO_IMAGES[countryLower];
    }
  }

  return HERO_IMAGE_DEFAULT;
}

const triggeredHeroGenerationSlugs = new Set<string>();

interface TriggerHeroImageGenerationArgs {
  citySlug: string;
  cityName: string;
  country: HeroImageCountry;
  isCrawler?: boolean;
}

export function triggerHeroImageGeneration({ citySlug, cityName, country, isCrawler }: TriggerHeroImageGenerationArgs): void {
  if (typeof window === 'undefined' || isCrawler) return;

  const normalized = normalizeSlugForS3(citySlug);
  if (!normalized || triggeredHeroGenerationSlugs.has(normalized)) return;
  triggeredHeroGenerationSlugs.add(normalized);

  LocalFocusGenerationObservabilityService.trackHeroImageGenerationTriggered({ slug: normalized, city: cityName, country });

  fetch(`/api/neighbourhood-images/${encodeURIComponent(normalized)}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'city', city: cityName, country }),
    keepalive: true,
  }).catch(() => {
    // Swallow — generation is advisory and the server is the source of truth.
  });
}
